import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, MapPin, Users, Pencil, Trash2, Clock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { todayStr, toISODate, STATUS_MAP, isOverdue, fmtDate } from "@/lib/taskConstants";
import { ENVIRONMENTS, ENV_ORDER, envKeyOf, tint } from "@/lib/environments";
import { EventDialog, EVENT_TYPE_MAP, RECURRENCE_MAP } from "@/components/calendar/EventDialog";
import { TimeGrid } from "@/components/calendar/TimeGrid";
import { CalendarGantt } from "@/components/calendar/CalendarGantt";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const loadHidden = () => { try { return JSON.parse(localStorage.getItem("atlas_cal_hidden")) || []; } catch { return []; } };

const EventChip = ({ event, color, onClick }) => (
  <button onClick={onClick} data-testid={`calendar-event-${event.id}`}
    className="w-full text-left text-[10px] leading-tight px-1.5 py-0.5 rounded truncate block text-white font-medium hover:opacity-80 transition-opacity"
    style={{ backgroundColor: event.color || color }}>
    {!event.all_day && event.start_time ? `${event.start_time} ` : ""}{EVENT_TYPE_MAP[event.event_type]?.icon} {event.title}
  </button>
);

const TASK_STATE_ICON = (t) => {
  if (t.status === "done") return "✅";
  if (isOverdue(t)) return "🔴";
  if (t.status === "in_progress") return "🔵";
  if (t.status === "review") return "🟣";
  return "⚪";
};

const TaskChip = ({ task, onClick }) => (
  <button onClick={onClick} data-testid={`calendar-gtask-${task.id}`}
    title={task.assignee_name ? `Responsable: ${task.assignee_name}` : "Sin responsable"}
    className="w-full text-left text-[10px] leading-tight px-1.5 py-0.5 rounded truncate flex items-center gap-1 border hover:opacity-80 transition-opacity"
    style={task.assignee_color
      ? { backgroundColor: `${task.assignee_color}1f`, borderColor: `${task.assignee_color}80` }
      : undefined}>
    <span className="text-[8px] leading-none shrink-0">{TASK_STATE_ICON(task)}</span>
    <span className={`truncate ${task.status === "done" ? "line-through text-muted-foreground" : ""}`}>{task.title}</span>
  </button>
);

const EventDetail = ({ event, sources, onClose, onEdit }) => {
  const qc = useQueryClient();
  const src = sources.find((s) => s.id === event.source);
  const type = EVENT_TYPE_MAP[event.event_type];
  const remove = async () => {
    try {
      await api.delete(`/events/${event.id}`);
      toast.success("Evento eliminado");
      qc.invalidateQueries({ queryKey: ["calendar"] });
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="event-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: event.color || src?.color }} />
            {event.title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">{type?.icon} {type?.label}</Badge>
            <Badge variant="outline">{src?.icon} {src?.name}</Badge>
            {event.recurrence && event.recurrence !== "none" && (
              <Badge variant="outline" data-testid="event-recurrence-badge">🔁 {RECURRENCE_MAP[event.recurrence]}</Badge>
            )}
          </div>
          <p className="flex items-center gap-2 text-muted-foreground">
            <Clock className="h-4 w-4" />
            {fmtDate(event.start_date)}{event.end_date !== event.start_date ? ` → ${fmtDate(event.end_date)}` : ""}
            {!event.all_day && event.start_time ? ` · ${event.start_time}${event.end_time ? `–${event.end_time}` : ""}` : " · Todo el día"}
          </p>
          {event.location && <p className="flex items-center gap-2 text-muted-foreground"><MapPin className="h-4 w-4" /> {event.location}</p>}
          {event.attendee_names?.length > 0 && (
            <p className="flex items-center gap-2 text-muted-foreground"><Users className="h-4 w-4" /> {event.attendee_names.join(", ")}</p>
          )}
          {event.description && <p className="whitespace-pre-wrap">{event.description}</p>}
          <p className="text-xs text-muted-foreground">Creado por {event.created_by_name}</p>
          {event.can_edit && (
            <div className="flex gap-2 pt-1">
              <Button variant="outline" size="sm" className="flex-1" onClick={onEdit} data-testid="event-edit-btn">
                <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
              </Button>
              <Button variant="outline" size="sm" className="flex-1 text-destructive" onClick={remove} data-testid="event-delete-btn">
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Eliminar
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function CalendarPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const now = new Date();
  const [view, setView] = useState("month");
  const [anchor, setAnchor] = useState(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
  const [hidden, setHidden] = useState(loadHidden);
  const [showTasks, setShowTasks] = useState(localStorage.getItem("atlas_cal_tasks") !== "0");
  const [envFilter, setEnvFilter] = useState(localStorage.getItem("atlas_cal_env") || "all");
  const [createOpen, setCreateOpen] = useState(false);
  const [createDate, setCreateDate] = useState(todayStr());
  const [createTime, setCreateTime] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);

  const cells = useMemo(() => {
    if (view === "day") return [new Date(anchor)];
    if (view === "week") {
      const monday = new Date(anchor);
      monday.setDate(anchor.getDate() - ((anchor.getDay() + 6) % 7));
      return Array.from({ length: 7 }, (_, i) => { const d = new Date(monday); d.setDate(monday.getDate() + i); return d; });
    }
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1 - ((first.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d; });
  }, [anchor, view]);

  const rangeStart = toISODate(cells[0]);
  const rangeEnd = toISODate(cells[cells.length - 1]);

  const { data, isLoading } = useQuery({
    queryKey: ["calendar", rangeStart, rangeEnd],
    queryFn: async () => (await api.get(`/calendar?start=${rangeStart}&end=${rangeEnd}`)).data,
  });
  const sources = data?.sources || [];
  const colorMap = Object.fromEntries(sources.map((s) => [s.id, s.color]));

  const presentEnvs = ENV_ORDER.filter((key) => sources.some((s) => s.id !== "personal" && envKeyOf(s) === key));
  const visibleSources = envFilter === "all"
    ? sources
    : sources.filter((s) => s.id !== "personal" && envKeyOf(s) === envFilter);
  const allowedIds = new Set(visibleSources.map((s) => s.id));

  const events = (data?.events || []).filter((e) => allowedIds.has(e.source) && !hidden.includes(e.source));
  const tasks = showTasks ? (data?.tasks || []).filter((t) => allowedIds.has(t.source) && !hidden.includes(t.source)) : [];

  const changeEnvFilter = (key) => {
    setEnvFilter(key);
    localStorage.setItem("atlas_cal_env", key);
  };

  const toggleSource = (id) => {
    const next = hidden.includes(id) ? hidden.filter((h) => h !== id) : [...hidden, id];
    setHidden(next);
    localStorage.setItem("atlas_cal_hidden", JSON.stringify(next));
  };
  const toggleTasks = (v) => {
    setShowTasks(v);
    localStorage.setItem("atlas_cal_tasks", v ? "1" : "0");
  };

  const navigateBy = (delta) => {
    const d = new Date(anchor);
    if (view === "day") d.setDate(d.getDate() + delta);
    else if (view === "week") d.setDate(d.getDate() + delta * 7);
    else d.setMonth(d.getMonth() + delta, 1);
    setAnchor(d);
  };

  const itemsForDay = (iso) => {
    const dayEvents = events
      .filter((e) => iso >= e.start_date && iso <= e.end_date)
      .sort((a, b) => (a.all_day === b.all_day ? (a.start_time || "").localeCompare(b.start_time || "") : a.all_day ? -1 : 1));
    const dayTasks = tasks.filter((t) => {
      const s = t.start_date || t.due_date;
      const e = t.due_date || t.start_date;
      return iso >= s && iso <= e;
    });
    return { dayEvents, dayTasks };
  };

  const openCreate = (iso, time = null) => {
    setCreateDate(iso);
    setCreateTime(time);
    setEditing(null);
    setCreateOpen(true);
  };
  const openTask = (t) => navigate(`/space/${t.workspace_id}/project/${t.project_id}?task=${t.id}`);

  const today = todayStr();
  const maxChips = 3;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 space-y-5" data-testid="calendar-page">
      <div className="flex flex-wrap items-center gap-2 animate-fade-up">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mr-2">Calendario</h1>
        <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => navigateBy(-1)} data-testid="gcal-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => navigateBy(1)} data-testid="gcal-next-btn"><ChevronRight className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setAnchor(new Date(now.getFullYear(), now.getMonth(), now.getDate()))} data-testid="gcal-today-btn">Hoy</Button>
        <span className="font-heading font-semibold text-lg" data-testid="gcal-label">
          {view === "day"
            ? `${cells[0].getDate()} ${MONTHS[cells[0].getMonth()]} ${cells[0].getFullYear()}`
            : view === "week"
            ? `${cells[0].getDate()} ${MONTHS[cells[0].getMonth()].slice(0, 3)} – ${cells[6].getDate()} ${MONTHS[cells[6].getMonth()].slice(0, 3)} ${cells[6].getFullYear()}`
            : `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <div className="grid grid-cols-4 rounded-lg bg-secondary p-0.5">
            {[["month", "Mes"], ["week", "Semana"], ["day", "Día"], ["gantt", "Gantt"]].map(([v, l]) => (
              <button key={v} data-testid={`gcal-view-${v}`} onClick={() => setView(v)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${view === v ? "bg-card shadow-sm" : "text-muted-foreground"}`}>
                {l}
              </button>
            ))}
          </div>
          <Button size="sm" onClick={() => openCreate(today)} data-testid="gcal-create-btn"><Plus className="h-4 w-4 mr-1" /> Crear</Button>
        </div>
      </div>

      {/* Filtro por entorno */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 animate-fade-up" data-testid="gcal-env-tabs">
        <button
          data-testid="gcal-env-tab-all"
          onClick={() => changeEnvFilter("all")}
          className={`px-4 h-8 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
            envFilter === "all" ? "bg-foreground text-background" : "bg-card text-muted-foreground hover:text-foreground shadow-sm"
          }`}
        >
          Todos
        </button>
        {presentEnvs.map((key) => {
          const env = ENVIRONMENTS[key];
          const active = envFilter === key;
          return (
            <button
              key={key}
              data-testid={`gcal-env-tab-${key}`}
              onClick={() => changeEnvFilter(key)}
              className="px-4 h-8 rounded-full text-xs font-semibold whitespace-nowrap transition-colors flex items-center gap-1.5"
              style={active
                ? { backgroundColor: env.color, color: "#FFFFFF" }
                : { backgroundColor: tint(env.color, 0.14), color: env.text }}
            >
              <span aria-hidden>{env.emoji}</span> {env.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-col lg:flex-row gap-5">
        <Card className="p-4 lg:w-56 shrink-0 h-fit space-y-3" data-testid="gcal-sources">
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Mis calendarios</h2>
          <div className="space-y-2">
            {visibleSources.map((s) => (
              <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`gcal-source-${s.id}`}>
                <Checkbox checked={!hidden.includes(s.id)} onCheckedChange={() => toggleSource(s.id)} data-testid={`gcal-source-check-${s.id}`} />
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                <span className="truncate flex-1">{s.icon} {s.name}</span>
                {s.id !== "personal" && <span className="text-xs shrink-0" title={ENVIRONMENTS[envKeyOf(s)]?.label} aria-hidden>{ENVIRONMENTS[envKeyOf(s)]?.emoji}</span>}
              </label>
            ))}
            {visibleSources.length === 0 && (
              <p className="text-xs text-muted-foreground">Sin espacios en este entorno.</p>
            )}
          </div>
          <div className="border-t pt-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={showTasks} onCheckedChange={toggleTasks} data-testid="gcal-tasks-check" />
              Mostrar tareas
            </label>
          </div>
        </Card>

        <div className="flex-1 min-w-0">
          {isLoading ? (
            <Skeleton className="h-[480px] rounded-xl" />
          ) : view === "gantt" ? (
            <CalendarGantt events={events} tasks={tasks} sources={sources} colorMap={colorMap}
              rangeStart={rangeStart} rangeEnd={rangeEnd} today={today}
              onEvent={setDetail} onTask={openTask} />
          ) : view !== "month" ? (
            <TimeGrid days={cells} events={events} tasks={tasks} colorMap={colorMap} today={today}
              onEvent={setDetail} onTask={openTask} onSlot={(iso, time) => openCreate(iso, time)}
              onEventMove={async (ev, patch) => {
                try {
                  await api.put(`/events/${ev.id}`, patch);
                  qc.invalidateQueries({ queryKey: ["calendar"] });
                } catch (e) {
                  toast.error(apiError(e));
                }
              }} />
          ) : (
            <div className="rounded-xl border bg-card overflow-hidden">
              <div className="grid grid-cols-7 border-b bg-secondary/40">
                {WEEKDAYS.map((d) => (
                  <div key={d} className="py-2 text-center text-xs font-semibold text-muted-foreground">
                    {d}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {cells.map((date, i) => {
                  const iso = toISODate(date);
                  const inMonth = view === "week" || date.getMonth() === anchor.getMonth();
                  const { dayEvents, dayTasks } = itemsForDay(iso);
                  const overflow = dayEvents.length + dayTasks.length - maxChips;
                  let shown = 0;
                  return (
                    <div key={iso} data-testid={`gcal-cell-${iso}`}
                      onClick={() => openCreate(iso)}
                      className={`min-h-[100px] border-b border-r p-1.5 cursor-pointer hover:bg-secondary/30 transition-colors ${inMonth ? "" : "bg-secondary/20"} ${(i + 1) % 7 === 0 ? "border-r-0" : ""}`}>
                      <span className={`text-xs inline-flex items-center justify-center h-5 w-5 rounded-full ${
                        iso === today ? "bg-primary text-primary-foreground font-semibold" : inMonth ? "" : "text-muted-foreground/50"}`}>
                        {date.getDate()}
                      </span>
                      <div className="space-y-1 mt-1" onClick={(e) => e.stopPropagation()}>
                        {dayEvents.map((ev) => (shown++ < maxChips) && (
                          <EventChip key={`${ev.id}-${ev.start_date}`} event={ev} color={colorMap[ev.source]} onClick={() => setDetail(ev)} />
                        ))}
                        {dayTasks.map((t) => (shown++ < maxChips) && (
                          <TaskChip key={t.id} task={t} onClick={() => openTask(t)} />
                        ))}
                        {overflow > 0 && <span className="text-[10px] text-muted-foreground px-1">+{overflow} más</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <EventDialog
        open={createOpen}
        onOpenChange={(o) => { setCreateOpen(o); if (!o) { setEditing(null); setCreateTime(null); } }}
        sources={sources}
        defaultDate={createDate}
        defaultTime={createTime}
        event={editing}
      />
      {detail && !editing && (
        <EventDetail
          event={detail}
          sources={sources}
          onClose={() => setDetail(null)}
          onEdit={() => { setEditing(detail); setDetail(null); setCreateOpen(true); }}
        />
      )}
    </div>
  );
}
